import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as React from 'react'

export const Route = createFileRoute('/tryon/$id')({
  component: TryonPage,
})

type TryonStatus = {
  id: string
  remixPlanId: string
  status: string
  resultUrl?: string
  createdAt: string
  updatedAt: string
}

const STATUS_LABELS: Record<string, string> = {
  queued: 'Queued for try-on',
  processing: 'Generating preview...',
  completed: 'Preview ready',
  failed: 'Try-on failed',
}

function TryonPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const [tryon, setTryon] = React.useState<TryonStatus | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const fetchTryon = React.useCallback(async () => {
    try {
      const res = await fetch(`/api/v1/tryons/${id}`)
      if (!res.ok) {
        if (res.status === 404) {
          throw new Error('Try-on result not found')
        }
        throw new Error('Failed to fetch try-on status')
      }
      const data = await res.json()
      // Result image is served through the ownership-checked proxy route;
      // the API contract deliberately never exposes the private storage key.
      setTryon({
        ...data,
        resultUrl:
          data.status === 'completed' ? `/api/v1/tryons/${id}/result-image` : undefined,
      })
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => {
    fetchTryon()
    // Poll for status updates when processing
    if (tryon?.status === 'queued' || tryon?.status === 'processing') {
      const interval = setInterval(fetchTryon, 3000)
      return () => clearInterval(interval)
    }
  }, [fetchTryon, tryon?.status])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted">Loading try-on preview...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
        <div className="rounded-lg bg-danger/10 p-4 text-center">
          <p className="text-danger font-medium">Error</p>
          <p className="text-sm text-danger/80 mt-1">{error}</p>
        </div>
        <button
          type="button"
          onClick={fetchTryon}
          className="rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          Try Again
        </button>
      </div>
    )
  }

  if (!tryon) {
    return null
  }

  const isProcessing = tryon.status === 'queued' || tryon.status === 'processing'
  const isComplete = tryon.status === 'completed'
  const isFailed = tryon.status === 'failed'

  return (
    <div className="ootopia-pwa-page ootopia-pwa-stack">
      <div className="ootopia-scan-intro flex items-end justify-between gap-4">
        <div>
          <p className="ootopia-section-kicker">Private preview</p>
          <h1 className="ootopia-scan-heading mt-2">
            See the look <em>on you.</em>
          </h1>
        </div>
        <span className="ootopia-private-badge shrink-0">Private result</span>
      </div>

      {/* Status card */}
      <div className="ootopia-pwa-surface p-5">
        <div className="flex items-center gap-3">
          {isProcessing && (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          )}
          {isComplete && (
            <div className="rounded-full bg-success/10 p-1">
              <svg
                className="h-4 w-4 text-success"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Success</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          )}
          {isFailed && (
            <div className="rounded-full bg-danger/10 p-1">
              <svg
                className="h-4 w-4 text-danger"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Error</title>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          )}
          <div>
            <p className="font-medium">{STATUS_LABELS[tryon.status] ?? tryon.status}</p>
            <p className="text-sm text-muted">{new Date(tryon.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Processing state */}
      {isProcessing && (
        <div className="ootopia-pwa-surface p-5">
          <h2 className="font-semibold mb-2">Generating Your Preview</h2>
          <p className="text-sm text-muted">
            We're creating a makeup preview based on your adapted recipe. This usually takes 15-45
            seconds.
          </p>
          <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full animate-pulse"
              style={{ width: '60%' }}
            ></div>
          </div>
        </div>
      )}

      {/* Before/After preview */}
      {isComplete && tryon.resultUrl && (
        <div className="flex flex-col gap-4">
          <div className="ootopia-pwa-surface ootopia-pwa-detail__body">
            <h2 className="font-semibold mb-4">Your Makeup Preview</h2>

            <div className="ootopia-pwa-detail">
              {/* Before — original selfie not available in tryon context */}
              <div className="relative aspect-square rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                <p className="text-sm text-muted text-center px-2">
                  Original photo
                  <br />
                  not available
                </p>
              </div>

              {/* After result */}
              <div className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                <img
                  src={tryon.resultUrl}
                  alt="Makeup preview"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <span className="rounded-full bg-primary/10 px-3 py-1 text-xs text-primary">
                Before
              </span>
              <span className="rounded-full bg-success/10 px-3 py-1 text-xs text-success">
                After
              </span>
            </div>
          </div>

          {/* AI disclosure */}
          <div className="ootopia-pwa-disclosure">
            <p className="text-xs text-muted leading-relaxed">
              <strong>AI-Generated Cosmetic Preview:</strong> This image shows a simulated makeup
              application based on your adapted recipe. Actual results may vary depending on product
              application, lighting, and skin conditions. This is not a guarantee of specific
              results.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                navigate({ to: '/remix/$id/plan', params: { id: tryon.remixPlanId } })
              }}
              className="flex-1 rounded-xl border border-primary py-3 text-primary font-medium"
            >
              View Recipe
            </button>
            <button
              type="button"
              onClick={() => {
                navigate({
                  to: '/publish/$id',
                  params: { id: tryon.remixPlanId },
                  search: { type: undefined },
                })
              }}
              className="flex-1 rounded-xl bg-primary py-3 text-primary-foreground font-medium"
            >
              Publish
            </button>
          </div>
        </div>
      )}

      {/* Failed state */}
      {isFailed && (
        <div className="ootopia-pwa-surface ootopia-pwa-detail__body">
          <h2 className="font-semibold mb-2">Try-On Failed</h2>
          <p className="text-sm text-muted">
            We couldn't generate your makeup preview. This could be due to:
          </p>
          <ul className="mt-2 space-y-1 text-sm text-muted">
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>No face detected in the photo</span>
            </li>
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>Poor image quality or lighting</span>
            </li>
            <li className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              />
              <span>Technical issue with the try-on service</span>
            </li>
          </ul>
          <button
            type="button"
            onClick={() => {
              navigate({ to: '/remix/$id/plan', params: { id: tryon.remixPlanId } })
            }}
            className="mt-4 w-full rounded-xl bg-primary py-3 text-primary-foreground font-medium"
          >
            Back to Recipe
          </button>
        </div>
      )}
    </div>
  )
}
